import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wifi, Wind, Tv, Users, Star } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";

const HERO = "/api/files/melograno/properties/8b8421f8-4da2-4dcf-8378-b600d11a08de.webp";
const LOFT_IMG = "/api/files/melograno/properties/5421ae39-1a5c-445a-b620-72075b0fcbf2.webp";
const BEDROOM_IMG = "/api/files/melograno/properties/e5803f71-4c9d-4bf5-b55f-c7b1b7fa3beb.webp";
const COURTYARD = "/api/files/melograno/properties/127335dd-f498-40f6-8654-30843c08c46e.webp";
const HAMMOCK = "/api/files/melograno/properties/39c4cdfd-51d5-40c5-8004-093f08a07252.webp";

export default function Home() {
  const { t, lang } = useLang();
  useSEO({
    lang, path: "/",
    title: lang === "en"
      ? "Il Melograno apARTments — Rooms & Lofts in Castellanza, Italy"
      : "Il Melograno apARTments — Camere e Loft a Castellanza (VA)",
    description: lang === "en"
      ? "En-suite rooms and designer lofts inside Il Melograno Cultural Association, since 1978. Wi-Fi, air conditioning, Chromecast. Book your stay in Castellanza."
      : "Camere con bagno privato e loft d'autore nell'Associazione Culturale Il Melograno, dal 1978. Wi-Fi, aria condizionata, Chromecast. Prenota il tuo soggiorno a Castellanza.",
  });
  const icons = [Wifi, Wind, Tv, Users];
  return (
    <PublicLayout>
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="mel-container pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow mb-6">{t.home.eyebrow}</div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] md:leading-[1.02] tracking-tight break-words">
              {t.home.titleA} <br className="hidden md:block" />
              <em className="text-[var(--mel-primary)]">{t.home.titleB}</em> <br className="hidden md:block" />
              {t.home.titleC} <span className="italic">{t.home.titleD}</span>
            </h1>
            <p className="mt-8 text-lg text-[var(--mel-muted)] leading-relaxed max-w-xl">{t.home.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/proprieta" className="mel-btn-primary" data-testid="hero-browse-btn">
                {t.home.browse} <ArrowRight size={18} />
              </Link>
              <Link to="/contatti" className="mel-btn-outline" data-testid="hero-contact-btn">
                {t.home.contact}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up">
            <div className="relative">
              <img src={HERO} alt="Il Melograno apARTments" className="w-full h-[520px] object-cover rounded-md" data-testid="hero-image" />
              <div className="absolute -bottom-8 -left-8 bg-[var(--mel-bg)] border border-[var(--mel-border)] p-6 rounded-md max-w-[240px] shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                <div className="flex items-center gap-1 text-[var(--mel-primary)] mb-2">
                  {[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor" />)}
                </div>
                <p className="text-sm font-serif italic leading-snug">{t.home.testimonial}</p>
                <div className="text-xs text-[var(--mel-muted)] mt-2">{t.home.recentGuests}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[var(--mel-border)] bg-[var(--mel-surface)]">
        <div className="mel-container py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {t.home.amenities.map((label, i) => {
            const Icon = icons[i];
            return (
              <div key={label} className="flex items-center gap-4">
                <Icon size={22} className="text-[var(--mel-primary)]" />
                <div className="text-sm">{label}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mel-container py-24 md:py-32" data-testid="home-story">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">{t.home.storyEyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
              {t.home.storyTitleA} <em className="text-[var(--mel-primary)]">{t.home.storyTitleB}</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-6">
            <p className="text-base md:text-lg text-[var(--mel-muted)] leading-relaxed">{t.home.storyBody}</p>
            <Link to="/storia" className="inline-flex items-center gap-2 mt-6 text-sm text-[var(--mel-primary)] hover:gap-3 transition-all" data-testid="story-more-link">
              {t.home.storyMore} <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-6 gap-4 md:gap-6">
          <div className="col-span-6 md:col-span-4 h-[280px] md:h-[400px] rounded-md overflow-hidden hover-lift">
            <img src={COURTYARD} alt="Cortile interno de Il Melograno apARTments a Castellanza con arte contemporanea e piante" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-3 md:col-span-2 h-[280px] md:h-[400px] rounded-md overflow-hidden hover-lift">
            <img src={LOFT_IMG} alt="Interno di un loft d'autore Il Melograno con cucina attrezzata e arte alle pareti" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-3 md:col-span-2 h-[280px] md:h-[320px] rounded-md overflow-hidden hover-lift">
            <img src={BEDROOM_IMG} alt="Corridoio con opere d'arte contemporanea e patchwork colorati nelle Stanze Art" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-6 md:col-span-2 h-[240px] md:h-[320px] rounded-md overflow-hidden hover-lift">
            <img src={HAMMOCK} alt="Amaca nel cortile del Melograno per momenti di relax immersi nella natura" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-6 md:col-span-2 h-[240px] md:h-[320px] rounded-md overflow-hidden hover-lift">
            <img src="/api/files/melograno/properties/f70d2de2-b841-474f-8154-48908d7eb0ce.webp" alt="Cortile Il Melograno" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="mel-container pb-24">
        <div className="rounded-md border border-[var(--mel-border)] p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center bg-[var(--mel-surface)]">
          <div>
            <div className="eyebrow mb-4">{t.home.ctaEyebrow}</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight">{t.home.ctaTitle}</h3>
            <p className="mt-4 text-[var(--mel-muted)]">{t.home.ctaSubtitle}</p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/proprieta" className="mel-btn-primary" data-testid="cta-book-btn">
              {t.home.ctaAvail} <ArrowRight size={18} />
            </Link>
            <Link to="/contatti" className="mel-btn-outline" data-testid="cta-contact-btn">{t.home.contact}</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
